/**
 * Ordine prioritario nel carrello.
 *
 * È un prodotto vero del negozio, che compare nel cassetto come una riga con un interruttore invece che
 * come articolo. Quando l'interruttore cambia, il prodotto viene aggiunto o tolto dal carrello con le
 * stesse chiamate che usa Horizon, e l'evento standard `shopify:cart:lines-update` viene emesso con la
 * stessa forma che usa il tema: così il cassetto si ridisegna e il contatore in alto si aggiorna da soli,
 * senza codice in più.
 *
 * Acceso di default: la prima volta che il cliente mette qualcosa nel carrello, l'ordine prioritario
 * entra insieme. Se lo spegne, resta spento per tutta la visita (sessionStorage). Se nel carrello resta
 * solo l'ordine prioritario, viene tolto: da solo non ha senso.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const SOURCE = 'sustena-priority';
const OPT_OUT_KEY = 'sustena-priority-off';
const SECTION_ID = 'cart-drawer-section';

/** @returns {{ variantId: string, defaultOn: boolean } | null} */
function config() {
  const host = document.querySelector('cart-drawer-component[data-priority-variant]');
  if (!(host instanceof HTMLElement) || !host.dataset.priorityVariant) return null;
  return { variantId: host.dataset.priorityVariant, defaultOn: host.dataset.priorityDefault === 'true' };
}

function optedOut() {
  try {
    return sessionStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

/** @param {boolean} value */
function setOptedOut(value) {
  try {
    if (value) sessionStorage.setItem(OPT_OUT_KEY, '1');
    else sessionStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* la memoria di sessione può mancare: si perde solo il ricordo della scelta */
  }
}

/** @returns {Promise<any>} */
function readCart() {
  return fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
}

let busy = false;

/**
 * Aggiunge o toglie l'ordine prioritario, passando dallo stesso evento che il tema usa per ogni
 * modifica al carrello: chi ascolta (cassetto, icona) fa il resto.
 * @param {'add' | 'remove'} action
 * @param {{ variantId: string, lineKey?: string }} target
 * @param {{ silent?: boolean }} [options] - silent: il cassetto non si apre da solo per questa modifica
 */
async function change(action, { variantId, lineKey }, { silent = false } = {}) {
  if (busy) return;
  busy = true;

  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({
    action,
    context: 'cart',
    lines: action === 'add' ? [{ merchandiseId: variantId, quantity: 1 }] : [{ id: lineKey ?? '', quantity: 0 }],
    promise: deferred.promise,
    detail: { source: SOURCE },
  });

  // Il cassetto si apre da solo a ogni "add": per un riallineamento silenzioso (al caricamento della
  // pagina) non deve. L'attributo viene letto in modo sincrono durante il dispatch, quindi basta
  // toglierlo per quell'istante.
  const drawer = document.querySelector('cart-drawer-component');
  const hadAutoOpen = silent && drawer?.hasAttribute('auto-open');
  if (hadAutoOpen) drawer.removeAttribute('auto-open');
  try {
    document.dispatchEvent(event);
  } finally {
    if (hadAutoOpen) drawer.setAttribute('auto-open', '');
  }

  try {
    const url = action === 'add' ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const body =
      action === 'add'
        ? { items: [{ id: Number(variantId), quantity: 1 }], sections: SECTION_ID, sections_url: location.pathname }
        : { id: lineKey, quantity: 0, sections: SECTION_ID, sections_url: location.pathname };

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    if (!response.ok || json.status) throw new Error(json.message || json.description || `Cart ${action} failed`);

    // /cart/add.js risponde con le righe aggiunte, non col carrello: il carrello serve intero
    const cart = json.items && json.item_count !== undefined ? json : await readCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: json.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  } catch (error) {
    console.warn('[sustena-priority]', error);
    deferred.reject(error);
  } finally {
    busy = false;
  }
}

/**
 * Cosa contiene il carrello rispetto all'ordine prioritario.
 * @param {any} cart
 * @param {string} variantId
 */
function inspect(cart, variantId) {
  const priority = cart.items.find((item) => String(item.variant_id) === variantId);
  const others = cart.items.filter((item) => String(item.variant_id) !== variantId);
  return { priority, others };
}

// L'interruttore nel cassetto
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.matches('[data-sustena-priority-toggle]')) return;

  const variantId = input.dataset.variant ?? '';
  if (input.checked) {
    setOptedOut(false);
    change('add', { variantId });
  } else {
    setOptedOut(true);
    change('remove', { variantId, lineKey: input.dataset.lineKey });
  }
});

/**
 * Riallinea il carrello alla regola: con altri articoli dentro e nessuna rinuncia, l'ordine prioritario
 * c'è; da solo, non c'è. Al caricamento copre chi arriva con il carrello già pieno da prima.
 * @param {boolean} allowAdd
 */
async function reconcile(allowAdd) {
  const settings = config();
  if (!settings || busy) return;

  const cart = await readCart();
  const { priority, others } = inspect(cart, settings.variantId);

  if (priority && others.length === 0) {
    change('remove', { variantId: settings.variantId, lineKey: priority.key }, { silent: true });
  } else if (!priority && others.length > 0 && settings.defaultOn && !optedOut() && allowAdd) {
    change('add', { variantId: settings.variantId }, { silent: true });
  }
}

reconcile(true).catch(() => {});

// Dopo ogni modifica fatta da altri: entra di default alla prima aggiunta, esce se resta da solo
document.addEventListener(CartLinesUpdateEvent.eventName, (event) => {
  const update = /** @type {any} */ (event);
  if (update.detail?.source === SOURCE) return;

  update.promise
    ?.then(() => reconcile(update.action === 'add'))
    .catch(() => {
      /* l'errore lo mostra già chi ha fatto la modifica */
    });
});
